import { Component, EventEmitter, HostListener, Input, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { CACHE_EXPIRATION_TIME, DESKTOP_SCREEN_SIZE } from 'app/common/personalConstants';
import { ClaimsTrackerService } from 'app/shared/services/claims-tracker.service';
import { LoggerService } from 'app/shared/services/logger.service';
import { SessionService } from 'app/shared/services/session.service';
import { UtilService } from 'app/shared/services/util.service';
import { environment } from 'environments/environment';
import { forkJoin, Observable } from 'rxjs';

interface CoverageElement {
    publicID: string;
    name: string;
    exposureLimit?: string;
    incidentLimit?: string;
    deductible?: string;
    description?: string;
    image?: string;
}

interface coverageResponse {
    coverages: CoverageElement[];
}

@Component({
  selector: 'is-coverages',
  templateUrl: './coverages.component.html',
  styleUrl: './coverages.component.scss',
  standalone: false
})
export class CoveragesComponent implements OnInit {
    @Input() claimNumber!: string;
    @Input() incidentDate!: string;
    @Input() displayUnregistered: boolean = false;
    @Output() goToTab = new EventEmitter<string>();
    isLoading: boolean = false;
    hasCoverages: boolean = false;
    hasCoverageProtection: boolean = false;
    windAndHailCoverage = {
        name: 'Wind and hail',
        responseName: 'Wind and hail deductible',
        image: 'wind-outline.svg',
        description: 'Your out-of-pocket costs in the event that your home is damaged by a windstorm and hail',
        deductibleValue: '',
        isVisible: false
    };
    allOtherPerilsCoverage = {
        name: 'All other perils',
        responseName: 'Per incident (all perils)',
        image: 'money-outline.svg',
        description: 'Your out-of-pocket costs for common causes of loss, for example, fire, water damages, theft or faling objects such as a tree',
        isVisible: false,
        deductibleValue: ''
    };
    protectionCoverages = [
        {
            name: 'Dwelling',
            responseName: 'Dwelling',
            image: 'home-outline.svg',
            description: 'Provides protection for the structure of your home, including the roofs, floors, walls, built-in appliances, ceilings, as well as any attached structures up to the coverage limit',
            isVisible: false,
            limitValue: ''
        },
        {
            name: 'Other structures',
            responseName: 'Other Structures',
            image: 'barn-outline.svg',
            description: 'Provides protection for structures on your property that are separate from your home, such as a tool shed or detached garage. Any structure that is enclosed by a fence on the same property or connected with a structure by utility line',
            isVisible: false,
            limitValue: ''
        },
        {
            name: 'Personal property',
            responseName: 'Personal Property',
            image: 'tshirt-outline.svg',
            description: 'Provides protection up to the coverage limit for your belongings or contents, including furniture, appliances, food, electronics, and clothing',
            isVisible: false,
            limitValue: ''
        },
        {
            name: 'Loss of use',
            responseName: 'Loss of Use',
            image: 'dolly-outline.svg',
            description: 'If a covered loss requires you to leave your home, we will reimburse you for the increase in living expenses, you and to maintain your normal standard of living up to the coverage limit',
            isVisible: false,
            limitValue: 'Selected'
        },
        {
            name: 'Personal liability',
            responseName: 'Personal Liability',
            image: 'lifeinsurance-outline.svg',
            description: 'Covers bodily injury and property damages for which an insured is held legally liable, except as limited or excluded by the policy. This coverage does not apply to bodily injury and property damages you or regular residents of your household sustain.',
            isVisible: false,
            limitValue: 'Selected'
        }
    ];
    coverageError = false;
    @Input() useCaching!: boolean;
    private policyNumber: string;
    private contactPublicId: string;
    private userType: string;

    private static readonly LOG_PREFIX = 'CoveragesComponent';

    constructor(
        private logger: LoggerService,
        private router: Router,
        private utils: UtilService,
        private sessionService: SessionService,
        private claimsTrackerService: ClaimsTrackerService
    ) {}


    ngOnInit(): void {
        if (!this.isLoading && this.claimNumber) {
            this.initializeSessionData();
            this.initializeLogger();

            const COVERAGES_CACHE_DATA = this.useCaching ? this.utils.getWithExpiry('COVERAGES_DATA') : undefined;
            this.isLoading = true;
            if (
                typeof COVERAGES_CACHE_DATA === 'undefined' ||
                COVERAGES_CACHE_DATA.id !== this.claimNumber
            ) {
                this.loadCoverages();
            } else {
                const coveragesResponse = COVERAGES_CACHE_DATA.value;
                this.loadCoveragesResponse(coveragesResponse);
            }
        }
    }

    loadCoverages(): void {
        this.claimsTrackerService.getPolicySummary(this.claimNumber).subscribe({
            next: (response) => {
                this.claimsTrackerService.getPolicyLocationRiskUnits(this.claimNumber, response.publicID).subscribe({
                    next: (policyLocationRiskResponse) => {
                        if (!policyLocationRiskResponse?.locationBasedRUs || !Array.isArray(policyLocationRiskResponse.locationBasedRUs)) {
                            this.isLoading = false;
                            this.coverageError = true;
                            return;
                        }
                        const requests: Observable<coverageResponse>[] = policyLocationRiskResponse.locationBasedRUs.map((locationBasedRU) =>
                            this.claimsTrackerService.getClaimCoverages(
                                this.claimNumber,
                                response.publicID,
                                locationBasedRU.publicID
                            ) as Observable<coverageResponse>
                        );

                        forkJoin(requests).subscribe({
                            next: (coveragesResponse) => {
                                if (!Array.isArray(coveragesResponse) || coveragesResponse.length === 0 ||
                                !coveragesResponse.some((item) => item?.coverages?.length > 0)) {
                                    this.isLoading = false;
                                    this.coverageError = true;
                                    return;
                                }
                                this.utils.setWithExpiry(
                                    'COVERAGES_DATA',
                                    coveragesResponse,
                                    CACHE_EXPIRATION_TIME,
                                    this.claimNumber
                                );
                                this.loadCoveragesResponse(coveragesResponse);
                            },
                            error: (error) => {
                                this.logger.error('Property Coverages: Error loading coverages', error);
                                this.isLoading = false;
                                this.coverageError = true;
                            }
                        });
                    },
                    error: (error) => {
                        this.logger.error('Property Coverages: Error fetching policies risk response', error);
                        this.isLoading = false;
                        this.coverageError = true;
                    }
                });
            },
            error: (error) => {
                this.logger.error('Property Coverages: Error loading policy summary', error);
                this.isLoading = false;
                this.coverageError = true;
            }
        });
    }

    private initializeLogger(): void {
        this.logger.setComponentName('CoveragesComponent');
        this.logger.setPageName('Coverages');
        this.logger.info(`${CoveragesComponent.LOG_PREFIX} - Component Initialized`, {
            claimNumber: this.claimNumber,
            policyNumber: this.policyNumber,
            userType: this.userType
        });
    }

    private initializeSessionData(): void {
        if (!this.claimNumber) {
            this.claimNumber = this.sessionService.getClaimNumber();
        }
        this.contactPublicId = this.sessionService.getSessionItem('claimsContactPublicId');
        this.policyNumber = this.sessionService.getPolicyNumber();
        this.userType = this.sessionService.getUserType();
    }

    loadCoveragesResponse(coveragesResponse: any[]): void {
        const coverages = coveragesResponse.flatMap(
            (coverage) => coverage?.coverages ?? []
          );

        const isWindHailPresent = coverages.find((item) => item.name === this.windAndHailCoverage.name);

        if (isWindHailPresent) {
            this.windAndHailCoverage.deductibleValue = isWindHailPresent.deductible;
            this.windAndHailCoverage.isVisible = true;
        }

        const isAllOtherPerilsVisible = coverages.find((item) => item.name === this.allOtherPerilsCoverage.name);

        if (isAllOtherPerilsVisible) {
            this.allOtherPerilsCoverage.deductibleValue = isAllOtherPerilsVisible.deductible;
            this.allOtherPerilsCoverage.isVisible = true;
        }

        const coveragesMap = new Map(
            coverages.map((item) => [item.name, (item.incidentLimit || item.exposureLimit)])
        );

        this.protectionCoverages.forEach((item) => {
            if (coveragesMap.has(item.responseName)) {
              item.isVisible = true;
              if (item.responseName !== 'Loss of Use' && item.responseName !== 'Personal Liability') {
                item.limitValue = coveragesMap.get(item.responseName);
              }
            }
        });

        this.isLoading = false;
        this.hasCoverages = true;
        this.hasCoverageProtection = this.protectionCoverages.length > 0;
    }

    goToMyAccount(): void {
        const accountSummaryUrl = this.displayUnregistered ? environment.NATIONWIDE.MY_ACCOUNT_LOGIN : environment.NATIONWIDE.accountSummaryPage;
        this.logger.info(`Property Coverages - Clicked on Your accounts`);
        this.navigateUrl(accountSummaryUrl);
    }

    referYourPolicy(): void {
        this.logger.info(`Property Coverages - Clicked on Refer Your Policy`);
        const accountSummaryUrl = this.displayUnregistered ? environment.NATIONWIDE.MY_ACCOUNT_LOGIN : environment.NATIONWIDE.accountSummaryPage;
        this.navigateUrl(accountSummaryUrl);
    }

    navigateUrl(url: string): void {
        window.location.href = url;
    }

    claimsProfessional(): void {
        this.logger.info(`Property Coverages - Clicked on Claims Professional`);
        if (window.innerWidth <= DESKTOP_SCREEN_SIZE) {
            this.router.navigate([`/property/tracker/support`]);
        } else {
            this.goToTab.emit('Support');
        }
    }

    @HostListener('window:resize')
    onResize(): void {
        if (window.innerWidth >= DESKTOP_SCREEN_SIZE) {
            this.sessionService.setSessionItem('tab', 'Coverages');
            this.router.navigate(['/property/tracker']);
        }
    }
}
